import { useEffect, useMemo, useState } from "react";

type UseChannelOrderOptions = {
  currentVoidId: string;
};

/**
 * Persisted channel meta-maps (step 5.1b — second slice of the channels
 * cluster):
 * - `chanOrder`: per-space channel ordering (`Record<spaceId, channelId[]>`),
 *   localStorage-backed, with the `channelOrderForSpace` memo for the active
 *   space (consumed by the `sidebarChannels` memo + ChannelSidebar).
 * - `channelTypeById`: cross-space channel-type cache so favorites render with
 *   the right view outside their own space, also localStorage-backed.
 *
 * Both maps are *written* by feeders that stay in the composition layer —
 * `onChannelList` (sets both), `handleChannelReorder` (chanOrder), and snapshot
 * hydration (chanOrder) — so the hook returns their setters. Inputs are just
 * `currentVoidId` (for the memo).
 */
export function useChannelOrder({ currentVoidId }: UseChannelOrderOptions) {
  const [chanOrder, setChanOrder] = useState<Record<string,string[]>>(() => {
    try { const s = localStorage.getItem('chanOrder'); return s ? JSON.parse(s) : {}; } catch { return {}; }
  });
  // Cache channel types across spaces so favorites can render correctly
  const [channelTypeById, setChannelTypeById] = useState<Record<string, string>>(() => {
    try { const raw = localStorage.getItem('channelTypeById'); return raw ? JSON.parse(raw) : {}; } catch { return {}; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('channelTypeById', JSON.stringify(channelTypeById));
    } catch {
      /* noop */
    }
  }, [channelTypeById]);
  useEffect(() => { try { localStorage.setItem('chanOrder', JSON.stringify(chanOrder)); } catch { /* noop */ } }, [chanOrder]);
  const channelOrderForSpace = useMemo(() => chanOrder[currentVoidId] || [], [chanOrder, currentVoidId]);

  return {
    chanOrder,
    setChanOrder,
    channelTypeById,
    setChannelTypeById,
    channelOrderForSpace,
  };
}
